import React, { useState, useEffect, useRef, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { DatePickerCustom } from "../../../components/ui/DatePickerCustom";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../../../helpers/index";

import { ReactComponent as CalendarIcon } from "../../../assets/img/icons/Calendar-Schedule.svg";
import { ReactComponent as LocationIcon } from "../../../assets/img/location-mark.svg";
import { ReactComponent as RemoveIcon } from "../../../assets/img/icons/times.svg";
import { ReactComponent as SearchIcon } from "../../../assets/img/icons/search-icon-black.svg";
import { fetchCourses } from "../../../redux/thunks/coursesThunk";
import { clearSearchResult } from "../../../redux/slices/coursesSlice";
import { YourLocationModal } from "../../../components/modals/YourLocationModal";
import { BookCourseModal } from "../../../components/modals/BookCourseModal";
import { trackFunnel } from "../../../helpers/analytics";
import { apiUrl } from "../../../data/constants";
import { initialPatLocation, initialPatStartDate, isPatCourse, mergePatLocationSearch } from "../../../helpers/patBooking";

export const SearchCourse = () => {
  const dispatch = useDispatch();
  const courses = useSelector((state) => state.courses.courses);
  // const selectedCourse = useSelector((state) => state.courses.selectedCourse);
  const { p_code } = useParams();
  const initialSearch = useMemo(() => ({
    location: initialPatLocation(p_code, window.location.search),
    start_date: initialPatStartDate(p_code, window.location.search),
  }), [p_code]);
  const initialLocation = initialSearch.location;
  const initialSearchCancelled = useRef(false);
  const initialAvailabilityRequest = useRef(null);
  const cancelInitialSearch = () => {
    initialSearchCancelled.current = true;
    initialAvailabilityRequest.current?.abort?.();
  };
  const [searchParams, setSearchParams] = useState({
    location: initialLocation,
    start_date: initialSearch.start_date,
  });
  useEffect(() => {
    initialSearchCancelled.current = false;
    if (isPatCourse(p_code)) setSearchParams(initialSearch);
  }, [initialSearch, p_code]);

  const [bookCourseModalOpen, setBookCourseModalOpen] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const suggestedLocations = ["London", "Edinburgh", "Birmingham", "Glasgow"];

  const navigate = useNavigate();

  // A course that only runs live online (every date at the "Virtual" centre, e.g. SMSTS, SSSTS) needs no location:
  // skip the location pop-up and show its dates straight away. A course with any classroom venue is unchanged.
  // "pending" until the check answers; any failure falls back to the location step, exactly as before.
  const [virtualCheck, setVirtualCheck] = useState("pending");
  useEffect(() => {
    let cancelled = false;
    const pcode = p_code || "DOM-PAK-REA";
    fetch(`${apiUrl}schedule?pcode=${encodeURIComponent(pcode)}`)
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return;
        const groups = (res && res.grouped) || [];
        const allVirtual =
          groups.length > 0 && groups.every((g) => g?.center?.name === "Virtual");
        // no dates anywhere: a location cannot help, so say so instead of asking for one
        setVirtualCheck(allVirtual ? "virtual" : groups.length === 0 ? "none" : "venues");
        if (allVirtual) trackFunnel("virtual_course_no_location");
        // online-only: load its dates now; no dates: load the empty result so the page knows (state.courses.not_found)
        if (allVirtual || groups.length === 0) dispatch(fetchCourses({ p_code: pcode }));
      })
      .catch(() => {
        if (!cancelled) setVirtualCheck("venues");
      });
    return () => {
      cancelled = true;
    };
  }, [p_code, dispatch]);

  const UK_TOWNS = ["Aberdeen","Basildon","Bath","Belfast","Birmingham","Blackburn","Blackpool","Bolton","Bournemouth","Bradford","Brighton","Bristol","Cambridge","Cardiff","Carlisle","Chelmsford","Cheltenham","Chester","Colchester","Coventry","Crawley","Derby","Doncaster","Dundee","Dunfermline","Edinburgh","Exeter","Falkirk","Fife","Glasgow","Gloucester","Greenock","Hamilton","Huddersfield","Hull","Inverness","Ipswich","Kilmarnock","Leeds","Leicester","Lincoln","Liverpool","Livingston","London","Luton","Manchester","Middlesbrough","Milton Keynes","Motherwell","Newcastle","Northampton","Norwich","Nottingham","Oldham","Oxford","Paisley","Perth","Peterborough","Plymouth","Portsmouth","Preston","Reading","Sheffield","Slough","Southampton","Southend-on-Sea","Stirling","Stockport","Stoke-on-Trent","Sunderland","Swansea","Swindon","Telford","Wakefield","Warrington","Watford","Wigan","Wolverhampton","York"];

  // closest known UK town for typo tolerance ("paisle" -> "Paisley")
  const closestTown = (input) => {
    const q = (input || "").trim().toLowerCase();
    if (q.length < 3) return null;
    const lev = (a, b) => {
      const m = a.length, n = b.length;
      const d = Array.from({ length: m + 1 }, (_, i) => [i].concat(Array(n).fill(0)));
      for (let j = 1; j <= n; j++) d[0][j] = j;
      for (let i = 1; i <= m; i++)
        for (let j = 1; j <= n; j++)
          d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      return d[m][n];
    };
    let best = null, bestScore = Infinity;
    for (const town of UK_TOWNS) {
      const t = town.toLowerCase();
      if (t === q || t.startsWith(q)) return town;
      const score = lev(q, t);
      if (score < bestScore) { bestScore = score; best = town; }
    }
    return bestScore <= 2 ? best : null;
  };

  // single geocode attempt, biased + restricted to the UK
  const geocodeOnce = (address) =>
    new Promise((res) => {
      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        res({ success: false, message: "NO_SDK" });
        return;
      }
      new window.google.maps.Geocoder().geocode(
        { address, region: "GB", componentRestrictions: { country: "GB" } },
        function (results, status) {
          if (status === "OK" && results && results.length) {
            let loc = results[0].geometry.location;
            res({ success: true, lat_lng: [loc.lat(), loc.lng()] });
          } else {
            res({ success: false, message: status });
          }
        },
      );
    });

  const geocodeSearch = async (location, isActive = () => true, isInitialSearch = false) => {
    setSearchError(null);
    const fetchAvailability = (payload) => {
      const request = dispatch(fetchCourses(payload));
      if (isInitialSearch) initialAvailabilityRequest.current = request;
      return request;
    };

    if (!location || location.length === 0) {
      fetchAvailability({ p_code: p_code ? p_code : "DOM-PAK-REA" });
      return false;
    }
    trackFunnel("location_searched");

    let result = await geocodeOnce(location);
    if (!isActive()) return { success: false, cancelled: true };

    if (!result.success) {
      const suggestion = closestTown(location);
      if (suggestion) {
        result = await geocodeOnce(suggestion + ", UK");
        if (!isActive()) return { success: false, cancelled: true };
        if (result.success) {
          trackFunnel("location_autocorrected");
          setSearchError('Showing courses near ' + suggestion + '.');
        }
      }
    }

    if (result.success) {
      fetchAvailability({
        p_code: p_code || "DOM-PAK-REA",
        user_cord: `&lat=${result.lat_lng[0]}&lng=${result.lat_lng[1]}`,
      });
    } else {
      trackFunnel("location_not_found");
      setSearchError(
        'We couldn\'t find "' + location + '" - showing all locations. Try a nearby town/city or a postcode.',
      );
      fetchAvailability({ p_code: p_code || "DOM-PAK-REA" });
    }

    return result;
  };

  // A supplied PAT city/postcode is already the visitor's location choice.
  // Wait briefly for the existing Maps SDK, then reuse the normal search path.
  const geocodeSearchRef = useRef(geocodeSearch);
  geocodeSearchRef.current = geocodeSearch;
  useEffect(() => {
    if (virtualCheck !== "venues" || !initialLocation) return;
    let cancelled = false;
    const loadInitialLocation = async () => {
      for (let attempt = 0; attempt < 50; attempt++) {
        if (cancelled || initialSearchCancelled.current) return;
        if (window.google?.maps?.Geocoder) break;
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (!cancelled && !initialSearchCancelled.current) {
        await geocodeSearchRef.current(initialLocation, () => !cancelled && !initialSearchCancelled.current, true);
      }
    };
    loadInitialLocation();
    return () => {
      cancelled = true;
      initialAvailabilityRequest.current?.abort?.();
    };
  }, [virtualCheck, initialLocation]);

  const searchServicesHandler = async () => {
    cancelInitialSearch();
    if (!searchParams?.location) {
      setIsError(true);
      return;
    }
    navigate({
      search: isPatCourse(p_code) ? mergePatLocationSearch(window.location.search, searchParams.location, searchParams.start_date) : `?location=${searchParams.location}&start_date=${new Date(
        searchParams?.start_date,
      ).getTime()}`,
    });

    await geocodeSearch(searchParams.location);
    setIsError(false);
  };

  const resetSearch = () => {
    cancelInitialSearch();
    setSearchError(null);
    dispatch(clearSearchResult());
    setSearchParams({
      location: "",
      start_date: new Date(),
    });
  };

  const modalSearchHandler = async (data) => {
    cancelInitialSearch();
    navigate({
      search: isPatCourse(p_code) ? mergePatLocationSearch(window.location.search, data?.user_city, searchParams.start_date) : `?location=${data?.user_city}&start_date=${new Date(
        searchParams?.start_date,
      ).getTime()}`,
    });
    setSearchParams({
      location: data?.user_city,
      start_date: new Date(),
    });
    let result = await geocodeSearch(data?.user_city);

    return result;
    // setLocationModalOpen(false);
  };

  return (
    <>
      {courses.length ? (
        <section className="section-dell-2">
          <div className="container container-900">
            <div className="radius-10 bg-white box-shadow2 mb-30 top-md-0 overflow-hidden">
              {searchError ? (
                <div style={{ padding: "10px 20px", fontSize: "13px", color: "#0d3b5b", background: "#fdf6e3", borderBottom: "1px solid #f0e0b0" }}>{searchError}</div>
              ) : null}
              {virtualCheck === "virtual" ? (
                <div className="px-20" style={{ padding: "14px 20px", fontSize: "15px", color: "#0d3b5b" }}>
                  <strong>Live online course:</strong> taught live by an instructor over Zoom, so there is no
                  location to choose. Pick a date below.
                </div>
              ) : (
              <div className="filters-block px-20">
                <div className="filters-block-row filters-block-row--12">
                  <div className="filters-block__item">
                    <div className="form-group max-w-350">
                      <label className="wrapp-input">
                        <span className="ico ico-20 mx-12">
                          <SearchIcon />
                        </span>
                        <div className="input px-40" type="search">
                          <div className="input-find">
                            <ul className="list-country">
                              <li>
                                <div className="link-counrty">
                                  {searchParams?.location}
                                </div>
                              </li>
                            </ul>
                            <div className="find-date">
                              {formatDate(searchParams?.start_date)}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn"
                          onClick={resetSearch}
                        >
                          <span className="ico ico-12">
                            <RemoveIcon />
                          </span>
                        </button>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              )}
              {courses[0].onSiteCourse == true ? (
                <div className="footer-add">
                  <div className="d-flex flex-column">
                    <h5 className="heading-5">
                      This course is also available onsite.
                    </h5>
                    <p className="def-text-1 color-text--600">
                      {courses[0].onSiteDescription}
                    </p>
                    {/* <p className="def-text-1 color-text--600">
                      Our in-house first aid training is fully accredited by the
                      First Aid Industry Body (FAIB).
                    </p> */}
                  </div>
                  <button
                    className="btn btn-success-outline
                   btn--md radius-5 ttu w-full-mob min-w-187"
                    onClick={(e) => {
                      e.preventDefault();
                      setBookCourseModalOpen(true);
                    }}
                  >
                    Book onsite
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {bookCourseModalOpen ? (
            <BookCourseModal
              isOpen={bookCourseModalOpen}
              close={() => setBookCourseModalOpen(false)}
            />
          ) : null}
        </section>
      ) : virtualCheck === "none" ? (
        <section className="section-dell">
          <div className="container container-900">
            <div className="radius-10 bg-white box-shadow2 mb-30 top-md-0" style={{ padding: "18px 20px", color: "#0d3b5b" }}>
              <strong>There are no dates scheduled for this course at the moment.</strong> Please call our team on{" "}
              <a href="tel:08081642780" className="btn tdu color-text--700">0808 164 2780</a> and we will let you
              know when the next course is running.
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className="section-dell">
            <div className="container container-900">
              <div className="radius-10 bg-white box-shadow2 mb-30 top-md-0">
                <div className="filters-block px-20">
                  <div className="filters-block-row filters-block-row--12">
                    <div className="filters-block__item">
                      <div className="form-group form-group--bottom">
                        <label className="form-group-title">
                          Your Location{" "}
                        </label>
                        <label className="wrapp-input">
                          <span className="ico ico-20 mx-12">
                            <LocationIcon />
                          </span>
                          <input
                            className={`input pl-40 ${isError && !searchParams?.location ? "error" : ""
                              }`}
                            type="text"
                            placeholder="Type your location"
                            value={searchParams.location}
                            onChange={(e) => {
                              cancelInitialSearch();
                              setSearchError(null);
                              setSearchParams((prev) => ({
                                ...prev,
                                location: e.target.value,
                              }));
                            }}
                          />
                        </label>
                        <ul className="list-locations mt-2">
                          {suggestedLocations.map((location, idx) => (
                            <li
                              className="location-item"
                              key={idx}
                              onClick={(e) => {
                                cancelInitialSearch();
                                setSearchParams((prev) => ({
                                  ...prev,
                                  location,
                                }));
                              }}
                            >
                              <button className="btn tdu">
                                <span className="text-1">{location}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="filters-block__item">
                      <div className="form-group form-group--bottom form-group--bottom-mob-none">
                        <label className="form-group-title">Date</label>
                        <label className="d-flex input align-items-center">
                          <CalendarIcon />
                          <DatePickerCustom
                            className="datapicker px-10"
                            value={isPatCourse(p_code) ? searchParams.start_date : new Date()}
                            onChange={(date) =>
                              setSearchParams((prev) => ({
                                ...prev,
                                start_date: date,
                              }))
                            }
                          />
                        </label>
                      </div>
                    </div>
                    <div className="filters-block__item mw-188 align-self-end">
                      <div className="form-group form-group--bottom form-group--bottom-mob-none relative">
                        <button
                          onClick={searchServicesHandler}
                          className="btn btn-success btn--md radius-5 ttu w-100"
                        >
                          Search
                        </button>
                        {searchError && !locationModalOpen ? (
                          <div style={{ position: "absolute", right: 0, top: "100%", marginTop: "8px", minWidth: "280px", zIndex: 5, fontSize: "13px", lineHeight: 1.4, color: "#0d3b5b", background: "#eaf6f8", border: "1px solid #bfe3ea", borderRadius: "6px", padding: "8px 10px", textAlign: "left" }}>
                            {searchError}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* the location pop-up only once we know the course has classroom venues (never for an online-only course) */}
            {virtualCheck === "venues" && !initialLocation ? (
              <YourLocationModal
                isOpen={locationModalOpen}
                close={() => setLocationModalOpen(false)}
                submit={modalSearchHandler}
                setOpen={setLocationModalOpen}
              />
            ) : null}
          </section>
        </>
      )}
    </>
  );
};
